/**
 * Carril K — lógica PURA de sesión/idle (sin window/localStorage → testeable).
 *
 * Modelo (fix del logout a ~15 min): el access token es CORTO (15m) por seguridad
 * y se renueva EN SILENCIO antes de expirar (ver lib/api scheduleSilentRefresh);
 * la vida de la SESIÓN la gobierna la INACTIVIDAD, no el TTL del access:
 *   - Base:          1 h de inactividad.
 *   - "Recordar":    12 h (opt-in; el admin del tenant podrá ajustarlo — fast-follow).
 *   - Cap duro:      el refresh token (30 días) sigue siendo el tope absoluto (backend).
 */

/** Ventana de inactividad DEFAULT del tenant (sin configurar): 1 hora. */
export const BASE_IDLE_MINUTES = 60;
/** "Recordar sesión" (opt-in) extiende a al menos 12 h. */
export const REMEMBER_IDLE_HOURS = 12;
/** Lista blanca de la ventana del tenant (min): 1h/4h/8h/12h. Espejo del server. */
export const SESSION_IDLE_WHITELIST = [60, 240, 480, 720] as const;
/** Refrescar el access este margen ANTES de que expire (evita el 401 reactivo). */
export const REFRESH_SKEW_MS = 60_000;
/** Piso del timer de refresh proactivo (evita loops apretados). */
export const MIN_REFRESH_DELAY_MS = 5_000;

/** Valida la ventana del tenant contra la lista blanca; fuera de rango → 60 (default seguro). */
export function clampTenantIdleMinutes(minutes?: number | null): number {
  return (SESSION_IDLE_WHITELIST as readonly number[]).includes(Number(minutes)) ? Number(minutes) : BASE_IDLE_MINUTES;
}

/**
 * Ventana de inactividad (ms). MODELO (Carril K fast-follow):
 *  - `tenantIdleMinutes` = ventana BASE del tenant (config del admin, 1/4/8/12h) —
 *    aplica a TODOS los usuarios del tenant. Fuera de la lista blanca → 1h.
 *  - "recordar sesión" (opt-in, default off) extiende a AL MENOS 12h.
 * (Es UX; el cap real sigue siendo el refresh token de 30d, server-side.)
 */
export function resolveIdleWindowMs(remember: boolean, tenantIdleMinutes?: number | null): number {
  const base = clampTenantIdleMinutes(tenantIdleMinutes);
  if (!remember) return base * 60 * 1000;
  return Math.max(base, REMEMBER_IDLE_HOURS * 60) * 60 * 1000;
}

/** True si `now - lastActivity` superó la ventana. Sin marca previa (0) = false. */
export function isExpiredByInactivity(lastActivity: number, windowMs: number, now: number): boolean {
  if (!lastActivity) return false;
  return now - lastActivity > windowMs;
}

/** base64url → string (browser: atob; node/tests: Buffer). */
function b64urlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(input.length / 4) * 4, '=');
  if (typeof atob === 'function') return atob(b64);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).Buffer.from(b64, 'base64').toString('binary');
}

/** exp del JWT en ms epoch, o null si no se puede leer (token opaco/malformado). */
export function parseJwtExpMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(b64urlDecode(parts[1])) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Delay (ms) hasta el próximo refresh PROACTIVO: `exp - skew - now`, con piso
 * `MIN_REFRESH_DELAY_MS`. null si el token no tiene exp legible (→ el path
 * reactivo del 401 sigue como red de seguridad).
 */
export function nextRefreshDelayMs(
  token: string | null | undefined, now: number,
  skewMs = REFRESH_SKEW_MS, minMs = MIN_REFRESH_DELAY_MS,
): number | null {
  const exp = parseJwtExpMs(token);
  if (exp == null) return null;
  return Math.max(minMs, exp - skewMs - now);
}
